'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import { useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';

gsap.registerPlugin(ScrollTrigger);

interface ScrollRevealProps {
  children: React.ReactNode;
  from?: gsap.TweenVars;
  to?: gsap.TweenVars;
  start?: string;
  end?: string;
  stagger?: number;
}

const ScrollReveal = ({
  children,
  from = { y: 150, opacity: 0 },
  to = { y: 0, opacity: 1 },
  start = 'top 90%',
  end = 'bottom 80%',
  stagger = 0.2,
}: ScrollRevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname(); // 👈 detect route changes

  useGSAP(
    () => {
      const ctx = gsap.context(() => {
        const items = gsap.utils.toArray<HTMLElement>('.reveal-item', ref.current);

        gsap.fromTo(
          items,
          from,
          {
            ...to,
            stagger,
            scrollTrigger: {
              trigger: ref.current,
              start,
              end,
              toggleActions: 'play none none reverse',
              once: false,
            },
          }
        );
      }, ref);

      return () => ctx.revert();
    },
    { scope: ref, dependencies: [pathname] } // 👈 rerun GSAP on route change
  );

  // 👇 Extra safety: refresh triggers after navigation & on resize
  useEffect(() => {
    const refresh = () => {
      setTimeout(() => ScrollTrigger.refresh(), 200); // delay for layout stability
    };

    refresh();
    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);

    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
    };
  }, [pathname]);

  return <div ref={ref}>{children}</div>;
};

export default ScrollReveal;
